/**
 * Browser camera/microphone helpers for meetings: capability detection, permission-aware device
 * access with plain-language failure states, device lists and locally kept device preferences.
 * Nothing here captures media on its own; every capture starts from an explicit user action or the
 * pre-join screen.
 */
import { useEffect, useState } from 'react';

export type DeviceKind = 'audioinput' | 'videoinput' | 'audiooutput';

/** Outcome of asking for one kind of device. */
export type AccessState = 'idle' | 'pending' | 'ready' | 'off' | 'denied' | 'dismissed' | 'missing' | 'busy' | 'insecure' | 'unsupported' | 'error';

export const mediaSupport = {
  get secure() {
    return typeof window !== 'undefined' && window.isSecureContext;
  },
  get capture() {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  },
  get screenShare() {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices && 'getDisplayMedia' in navigator.mediaDevices && !isMobileBrowser();
  },
  get speakerSelection() {
    return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
  },
};

function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile !== undefined) return uaData.mobile;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/* ------------------------------------------------------------- preferences */

export interface MediaPrefs {
  micOn: boolean;
  camOn: boolean;
  audioInput?: string;
  videoInput?: string;
  audioOutput?: string;
}

const PREFS_KEY = 'dw-meeting-devices';

/** Device choices are kept on this device only (they are meaningless elsewhere). */
export function loadPrefs(): MediaPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<MediaPrefs>;
      return { micOn: p.micOn ?? true, camOn: p.camOn ?? true, audioInput: p.audioInput, videoInput: p.videoInput, audioOutput: p.audioOutput };
    }
  } catch {
    // Storage may be unavailable; defaults apply.
  }
  return { micOn: true, camOn: true };
}

export function savePrefs(p: MediaPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // Not fatal.
  }
}

/* -------------------------------------------------------------- permissions */

async function permissionState(name: 'camera' | 'microphone'): Promise<PermissionState | 'unknown'> {
  try {
    const s = await navigator.permissions.query({ name: name as PermissionName });
    return s.state;
  } catch {
    return 'unknown';
  }
}

/** True when the browser already allows both devices, so a preview can start without a prompt. */
export async function alreadyAllowed(): Promise<boolean> {
  const [cam, mic] = await Promise.all([permissionState('camera'), permissionState('microphone')]);
  return cam === 'granted' && mic === 'granted';
}

async function classify(err: unknown, kind: 'camera' | 'microphone'): Promise<AccessState> {
  const name = (err as DOMException)?.name ?? '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    if (!mediaSupport.secure) return 'insecure';
    const s = await permissionState(kind);
    return s === 'denied' ? 'denied' : s === 'prompt' ? 'dismissed' : 'denied';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') return 'missing';
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') return 'busy';
  if (name === 'SecurityError') return 'insecure';
  // Browsers that can't ask for permission at all (e.g. embedded or restricted) report this.
  if (name === 'NotSupportedError') return mediaSupport.secure ? 'unsupported' : 'insecure';
  return 'error';
}

export interface AcquireResult {
  stream: MediaStream | null;
  audio: AccessState;
  video: AccessState;
}

/**
 * Requests the camera and/or microphone. Asks for both together (one browser prompt), then falls
 * back to each device on its own so a missing camera doesn't block the microphone.
 */
export async function acquire(opts: { audio: boolean; video: boolean; audioInput?: string; videoInput?: string }): Promise<AcquireResult> {
  if (!mediaSupport.capture) {
    const s: AccessState = mediaSupport.secure ? 'unsupported' : 'insecure';
    return { stream: null, audio: opts.audio ? s : 'off', video: opts.video ? s : 'off' };
  }
  const audioC: MediaTrackConstraints | false = opts.audio ? { deviceId: opts.audioInput ? { ideal: opts.audioInput } : undefined, echoCancellation: true, noiseSuppression: true } : false;
  const videoC: MediaTrackConstraints | false = opts.video
    ? { deviceId: opts.videoInput ? { ideal: opts.videoInput } : undefined, width: { ideal: 1280 }, height: { ideal: 720 } }
    : false;
  if (!audioC && !videoC) return { stream: null, audio: 'off', video: 'off' };
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: audioC, video: videoC });
    return { stream, audio: opts.audio ? 'ready' : 'off', video: opts.video ? 'ready' : 'off' };
  } catch (err) {
    const name = (err as DOMException)?.name ?? '';
    // A refused prompt applies to both; don't prompt again.
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError' || name === 'NotSupportedError') {
      const [a, v] = await Promise.all([opts.audio ? classify(err, 'microphone') : 'off', opts.video ? classify(err, 'camera') : 'off']);
      return { stream: null, audio: a as AccessState, video: v as AccessState };
    }
    if (!(audioC && videoC)) {
      const s = await classify(err, audioC ? 'microphone' : 'camera');
      return { stream: null, audio: audioC ? s : 'off', video: videoC ? s : 'off' };
    }
    // Try each device separately.
    const stream = new MediaStream();
    let audio: AccessState = 'off';
    let video: AccessState = 'off';
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: audioC });
      s.getTracks().forEach((t) => stream.addTrack(t));
      audio = 'ready';
    } catch (e) {
      audio = await classify(e, 'microphone');
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: videoC });
      s.getTracks().forEach((t) => stream.addTrack(t));
      video = 'ready';
    } catch (e) {
      video = await classify(e, 'camera');
    }
    return { stream: stream.getTracks().length ? stream : null, audio, video };
  }
}

export function stopStream(stream: MediaStream | null | undefined) {
  stream?.getTracks().forEach((t) => t.stop());
}

/** Plain-language explanation for a device problem, or null when there is nothing to explain. */
export function accessMessage(kind: 'camera' | 'microphone' | 'both', state: AccessState): string | null {
  const Device = kind === 'camera' ? 'Camera' : kind === 'microphone' ? 'Microphone' : 'Camera and microphone';
  const your = kind === 'both' ? 'camera and microphone' : kind;
  const any = kind === 'both' ? 'camera or microphone' : kind;
  const verb = kind === 'both' ? 'are' : 'is';
  switch (state) {
    case 'denied':
      return `${Device} access is blocked for this site. Allow it from the camera icon in your browser's address bar or in site settings, then try again.`;
    case 'dismissed':
      return `${Device} access wasn't allowed. Select Try again and choose Allow when your browser asks.`;
    case 'missing':
      return `No ${any} was found. Connect one and try again, or join without it.`;
    case 'busy':
      return `Your ${your} ${verb} being used by another app or couldn't start. Close the other app and try again.`;
    case 'insecure':
      return `${Device} access needs a secure (https) connection. Open this page from its https address.`;
    case 'unsupported':
      return `This browser can't use a ${any}. You can still join and use meeting chat.`;
    case 'error':
      return `Your ${your} couldn't start. Try again or choose another device.`;
    default:
      return null;
  }
}

export const isProblem = (s: AccessState) => !['idle', 'pending', 'ready', 'off'].includes(s);

/* ------------------------------------------------------------------ devices */

export function useDevices(enabled: boolean) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  useEffect(() => {
    if (!enabled || !navigator.mediaDevices?.enumerateDevices) return;
    let alive = true;
    const load = () =>
      navigator.mediaDevices
        .enumerateDevices()
        .then((list) => alive && setDevices(list.filter((d) => d.deviceId)))
        .catch(() => undefined);
    void load();
    navigator.mediaDevices.addEventListener?.('devicechange', load);
    return () => {
      alive = false;
      navigator.mediaDevices.removeEventListener?.('devicechange', load);
    };
  }, [enabled]);
  return {
    audioInputs: devices.filter((d) => d.kind === 'audioinput'),
    videoInputs: devices.filter((d) => d.kind === 'videoinput'),
    audioOutputs: devices.filter((d) => d.kind === 'audiooutput'),
  };
}

export function deviceLabel(d: MediaDeviceInfo, i: number) {
  if (d.label) return d.label;
  return d.kind === 'videoinput' ? `Camera ${i + 1}` : d.kind === 'audioinput' ? `Microphone ${i + 1}` : `Speaker ${i + 1}`;
}

/** Live microphone level (0–1) for a preview stream. */
export function useAudioLevel(stream: MediaStream | null) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    const track = stream?.getAudioTracks()[0];
    if (!track || typeof AudioContext === 'undefined') {
      setLevel(0);
      return;
    }
    const ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(new MediaStream([track]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    src.connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    let raf = 0;
    let last = 0;
    const tick = (t: number) => {
      raf = requestAnimationFrame(tick);
      if (t - last < 80) return;
      last = t;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (const v of data) sum += ((v - 128) / 128) ** 2;
      setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      src.disconnect();
      void ctx.close().catch(() => undefined);
    };
  }, [stream]);
  return level;
}
